import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { exportToCSV } from '../../../services/exportService';
import { useNavigate } from 'react-router-dom';

const EvidenceLayer = ({ driverType, driverName, onBackToDrivers }) => {
  const navigate = useNavigate();

  // Mock evidence data based on driver type
  const getEvidenceData = () => {
    switch (driverType) {
      case 'revenue':
        return {
          title: `${driverName} - Evidence Layer`,
          description: 'Detailed transaction and activity evidence supporting revenue metrics',
          dataPoints: [
            { id: 'TXN-2026-001', date: '2026-02-10', customer: 'Global Tech Corp', amount: 125000, status: 'Completed', type: 'Contract Renewal' },
            { id: 'TXN-2026-002', date: '2026-02-09', customer: 'Innovation Systems Ltd', amount: 98000, status: 'Completed', type: 'New Sale' },
            { id: 'TXN-2026-003', date: '2026-02-08', customer: 'Digital Ventures Inc', amount: 75000, status: 'Pending', type: 'Upsell' },
            { id: 'TXN-2026-004', date: '2026-02-07', customer: 'Enterprise Solutions Co', amount: 150000, status: 'Completed', type: 'Contract Renewal' },
            { id: 'TXN-2026-005', date: '2026-02-06', customer: 'Cloud Partners LLC', amount: 62000, status: 'Completed', type: 'New Sale' },
          ]
        };
      case 'cost':
        return {
          title: `${driverName} - Evidence Layer`,
          description: 'Detailed expense records and cost allocation evidence',
          dataPoints: [
            { id: 'EXP-2026-001', date: '2026-02-10', vendor: 'Tech Supplier Inc', amount: 45000, status: 'Paid', category: 'Technology' },
            { id: 'EXP-2026-002', date: '2026-02-09', vendor: 'Office Solutions Ltd', amount: 12000, status: 'Paid', category: 'Operations' },
            { id: 'EXP-2026-003', date: '2026-02-08', vendor: 'Marketing Agency Co', amount: 28000, status: 'Pending', category: 'Marketing' },
            { id: 'EXP-2026-004', date: '2026-02-07', vendor: 'Cloud Services Provider', amount: 35000, status: 'Paid', category: 'Technology' },
            { id: 'EXP-2026-005', date: '2026-02-06', vendor: 'HR Consulting Group', amount: 18000, status: 'Paid', category: 'Personnel' },
          ]
        };
      case 'sales':
        return {
          title: `${driverName} - Evidence Layer`,
          description: 'Product-level sales transactions and order details',
          dataPoints: [
            { id: 'ORD-2026-001', date: '2026-02-10', product: 'Enterprise Suite Pro', quantity: 50, amount: 250000, customer: 'Global Tech Corp' },
            { id: 'ORD-2026-002', date: '2026-02-09', product: 'Cloud Platform Basic', quantity: 120, amount: 180000, customer: 'Innovation Systems Ltd' },
            { id: 'ORD-2026-003', date: '2026-02-08', product: 'Analytics Module', quantity: 75, amount: 112500, customer: 'Digital Ventures Inc' },
            { id: 'ORD-2026-004', date: '2026-02-07', product: 'Security Package', quantity: 30, amount: 90000, customer: 'Enterprise Solutions Co' },
            { id: 'ORD-2026-005', date: '2026-02-06', product: 'Support Plan Premium', quantity: 100, amount: 150000, customer: 'Cloud Partners LLC' },
          ]
        };
      default:
        return {
          title: `${driverName} - Evidence Layer`,
          description: 'Detailed supporting evidence and transaction records',
          dataPoints: [
            { id: 'REC-2026-001', date: '2026-02-10', description: 'Q4 Performance Review', value: 95, status: 'Completed' },
            { id: 'REC-2026-002', date: '2026-02-09', description: 'Project Milestone Achieved', value: 88, status: 'Completed' },
            { id: 'REC-2026-003', date: '2026-02-08', description: 'Quality Audit', value: 92, status: 'In Progress' },
            { id: 'REC-2026-004', date: '2026-02-07', description: 'Customer Satisfaction Survey', value: 87, status: 'Completed' },
            { id: 'REC-2026-005', date: '2026-02-06', description: 'Efficiency Assessment', value: 91, status: 'Completed' },
          ]
        };
    }
  };

  const evidenceData = getEvidenceData();

  const handleExportCSV = () => {
    const csvData = evidenceData?.dataPoints?.map(item => ({
      ID: item?.id,
      Date: item?.date,
      ...item
    }));

    exportToCSV({
      title: `Evidence_Layer_${driverType}_${Date.now()}`,
      data: csvData
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'paid':
        return 'text-success bg-success/10';
      case 'pending': case'in progress':
        return 'text-warning bg-warning/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center gap-2 text-sm" aria-label="Evidence breadcrumb">
        <button
          onClick={() => navigate('/executive-view')}
          className="text-primary hover:text-primary/80 transition-smooth font-medium focus-ring rounded px-1"
        >
          Executive View
        </button>
        <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
        <button
          onClick={() => navigate('/drill-down-analysis')}
          className="text-primary hover:text-primary/80 transition-smooth font-medium focus-ring rounded px-1"
        >
          Data Driver
        </button>
        <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
        <button
          onClick={onBackToDrivers}
          className="text-primary hover:text-primary/80 transition-smooth font-medium focus-ring rounded px-1"
        >
          Drill Down
        </button>
        <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
        <span className="text-foreground font-medium">Evidence</span>
      </nav>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-md bg-accent/10">
            <Icon name="FileText" size={20} color="var(--color-accent)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">{evidenceData?.title}</h2>
            <p className="text-sm text-muted-foreground font-caption">{evidenceData?.description}</p>
          </div>
        </div>
        <Button
          onClick={handleExportCSV}
          variant="outline"
          size="sm"
          iconName="Download"
        >
          Export CSV
        </Button>
      </div>

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                {Object.keys(evidenceData?.dataPoints?.[0] || {})?.map((key) => (
                  <th key={key} className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {evidenceData?.dataPoints?.map((row, index) => (
                <tr key={index} className="hover:bg-muted/30 transition-smooth">
                  {Object.entries(row)?.map(([key, value], cellIndex) => (
                    <td key={cellIndex} className="px-4 py-3 text-sm">
                      {key === 'status' ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(value)}`}>
                          {value}
                        </span>
                      ) : key === 'amount' || key === 'value' ? (
                        <span className="font-semibold text-foreground font-data">
                          {typeof value === 'number' && key === 'amount' ? `$${value?.toLocaleString()}` : value}
                        </span>
                      ) : (
                        <span className="text-foreground">{value}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-1">Evidence Layer Information</h4>
            <p className="text-xs text-muted-foreground">
              This layer provides granular transaction-level evidence supporting the metrics displayed in the Data Drivers view. 
              Each record represents actual system data that contributes to the aggregated KPIs and analytics.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvidenceLayer;